import React, { useState, useEffect, useMemo } from 'react';
import {
  FiCopy,
  FiCheck,
  FiServer,
  FiWifi,
  FiKey,
  FiSettings,
  FiCheckCircle,
  FiArrowRight,
  FiRefreshCw,
} from 'react-icons/fi';
import {
  useGetDevicesWithConnections,
  useGetApplicationEntitiesPageable,
} from '../../../helpers/api/pacs/pacs';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useAdress } from '../../../helpers/api/users/users';
import { useMe } from '../../../helpers/api/users/users';
import { usePermissions } from '../../../helpers/acl/permissions';
import { useTranslation } from 'react-i18next';

const extractSubdomain = (hostname: string) => {
  let effectiveHostname = hostname;
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0'
  ) {
    // TODO: ask abdou
    effectiveHostname = 'dev.deepecho.io';
  }

  const parts = effectiveHostname.split('.');
  if (parts.length > 2) {
    return parts[0];
  }
  return '';
};

interface ConnectionDataModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ConnectionDataModal: React.FC<ConnectionDataModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('Dashboard');
  const {
    data: devices,
    isLoading: isLoadingDevices,
    error: devicesError,
    refetch: refetchDevices,
  } = useGetDevicesWithConnections();
  const {
    data: aesData,
    isLoading: isLoadingAEs,
    refetch: refetchAEs,
  } = useGetApplicationEntitiesPageable({
    page: 0,
    size: 1,
    sort: 'id',
    direction: 'ASC',
    deviceId: devices?.[0]?.id || null,
  });
  const subdomain = useMemo(() => {
    if (typeof window !== 'undefined') {
      return extractSubdomain(window.location.hostname);
    }
    return '';
  }, []);
  const {
    data: address,
    isLoading: isLoadingAddress,
    error: errorAddress,
  } = useAdress(subdomain);
  const { data: userData } = useMe();
  const { can } = usePermissions();
  const canManagePacs = can('pacs_device.manage');

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const navigate = useNavigate();

  // Refetch devices and AEs when modal opens
  useEffect(() => {
    if (isOpen) {
      refetchDevices();
    }
  }, [isOpen, refetchDevices]);

  // Refetch AEs when we have a device
  useEffect(() => {
    if (isOpen && devices?.[0]?.id) {
      refetchAEs();
    }
  }, [isOpen, devices, refetchAEs]);

  const isLoading = isLoadingDevices || isLoadingAEs || isLoadingAddress;
  const error = devicesError || errorAddress;

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const device = devices?.[0];
  const connection = device?.connections?.[0];
  const ae = aesData?.content?.[0]; // Get AE from the pageable endpoint

  // Display connection data even if AE is not available yet
  const hostname = connection?.hostname || t('connectionData.na');
  const port = connection?.port?.toString() || t('connectionData.na');
  const aeTitle = ae?.aeTitle || t('connectionData.na');
  const deviceName =
    device?.deviceName || t('connectionData.defaultDeviceName');

  const handleManageDevices = () => {
    onClose();
    navigate('/organization?pacsTab=pacs');
  };

  const handleViewPacsInbox = () => {
    onClose();
    navigate('/pacs-inbox');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      className="bg-card-color-200 dark:bg-gray-900"
    >
      <Modal.Header title={t('connectionData.title')} onClose={onClose} />

      <Modal.Body>
        {isLoading ? (
          <div className="p-6">
            <div className="text-center py-12">
              <FiRefreshCw
                size={48}
                className="text-slate-400 dark:text-gray-500 mx-auto mb-4 animate-spin"
              />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {t('connectionData.loadingTitle')}
              </h3>
              <p className="text-slate-600 dark:text-gray-400">
                {t('connectionData.loadingBody')}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="text-center py-12">
              <FiServer
                size={48}
                className="text-slate-400 dark:text-gray-500 mx-auto mb-4"
              />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {devicesError
                  ? t('connectionData.stillInitializing')
                  : t('connectionData.connectionError')}
              </h3>
              <p className="text-slate-600 dark:text-gray-400 mb-4">
                {devicesError
                  ? t('connectionData.pacsStarting')
                  : errorAddress
                    ? t('connectionData.unableToFetchAddress')
                    : t('connectionData.issueLoading')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('connectionData.tryAgain')}
              </p>
            </div>
          </div>
        ) : !device || !connection ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FiServer size={48} className="text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('connectionData.noDevice')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t('connectionData.configureDevice')}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Intro Section */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <FiServer
                  className="text-blue-600 dark:text-blue-400 mt-0.5"
                  size={16}
                />
                <div className="flex-1">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>{t('connectionData.defaultDevice')}</strong>{' '}
                    <span className="font-mono">{deviceName}</span> —
                    {canManagePacs && (
                      <button
                        onClick={handleManageDevices}
                        className="underline hover:text-blue-900 dark:hover:text-blue-300 font-medium ml-1"
                      >
                        {t('connectionData.configureNewDevice')}
                      </button>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-card-color-100 dark:bg-gray-800 rounded-lg p-3 border border-slate-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white mb-2 flex items-center gap-2">
                <FiCheckCircle className="text-green-500" size={16} />
                {t('connectionData.howToTitle')}
              </h3>
              <ol className="space-y-1.5 text-xs text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-blue-600 dark:text-blue-400 min-w-[16px]">
                    1.
                  </span>
                  <span>
                    {t('connectionData.steps.fillIpPre')}{' '}
                    <strong>{t('connectionData.fields.ipAddress')}</strong>{' '}
                    {t('connectionData.steps.fillIpPost')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-blue-600 dark:text-blue-400 min-w-[16px]">
                    2.
                  </span>
                  <span>
                    {t('connectionData.steps.enterPre')}{' '}
                    <strong>{t('connectionData.fields.portNumber')}</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-blue-600 dark:text-blue-400 min-w-[16px]">
                    3.
                  </span>
                  <span>
                    {t('connectionData.steps.usePre')}{' '}
                    <strong>{t('connectionData.fields.calledAeTitle')}</strong>{' '}
                    {t('connectionData.steps.usePost')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-green-600 dark:text-green-400 min-w-[16px]">
                    4.
                  </span>
                  <span>
                    <strong>{t('connectionData.steps.verifyStrong')}</strong>{' '}
                    {t('connectionData.steps.verifyPost')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-green-600 dark:text-green-400 min-w-[16px]">
                    5.
                  </span>
                  <span>
                    {t('connectionData.steps.sendPre')}{' '}
                    <strong>{t('connectionData.steps.sendStrong')}</strong>{' '}
                    {t('connectionData.steps.sendPost')}{' '}
                    <button
                      onClick={handleViewPacsInbox}
                      className="underline hover:text-green-800 dark:hover:text-green-300 font-medium"
                    >
                      {t('connectionData.pacsInbox')}
                    </button>
                  </span>
                </li>
              </ol>
            </div>

            {/* Connection Values */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {t('connectionData.connectionDetails')}
              </h4>

              {/* IP Address */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-slate-200 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-1.5 rounded">
                      <FiServer
                        className="text-blue-600 dark:text-blue-400"
                        size={14}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {t('connectionData.fields.ipAddress')}
                      </p>
                      <code className="text-sm font-mono font-bold text-gray-900 dark:text-white block truncate">
                        {address?.data?.static_ips &&
                        address?.data?.static_ips.length > 0
                          ? address?.data?.static_ips
                              .map(
                                (ip: { address: string }) => `${ip?.address}`
                              )
                              .join(', ')
                          : `${hostname}`}
                      </code>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        address?.data?.static_ips &&
                          address?.data?.static_ips.length > 0
                          ? address?.data?.static_ips
                              .map(
                                (ip: { address: string }) => `${ip?.address}`
                              )
                              .join(', ')
                          : hostname,
                        'hostname'
                      )
                    }
                    className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 transition-colors flex-shrink-0"
                    title={t('connectionData.copy')}
                  >
                    {copiedField === 'hostname' ? (
                      <FiCheck size={14} className="text-green-500" />
                    ) : (
                      <FiCopy size={14} />
                    )}
                  </button>
                </div>
              </div>

              {/* Port */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-slate-200 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="bg-green-100 dark:bg-green-900/30 p-1.5 rounded">
                      <FiWifi
                        className="text-green-600 dark:text-green-400"
                        size={14}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {t('connectionData.fields.portNumber')}
                      </p>
                      <code className="text-sm font-mono font-bold text-gray-900 dark:text-white block truncate">
                        {port}
                      </code>
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(port, 'port')}
                    className="p-1.5 rounded bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400 transition-colors flex-shrink-0"
                    title={t('connectionData.copy')}
                  >
                    {copiedField === 'port' ? (
                      <FiCheck size={14} className="text-green-500" />
                    ) : (
                      <FiCopy size={14} />
                    )}
                  </button>
                </div>
              </div>

              {/* AE Title */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-slate-200 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-1.5 rounded">
                      <FiKey
                        className="text-purple-600 dark:text-purple-400"
                        size={14}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {t('connectionData.fields.calledAeTitle')}
                      </p>
                      <code className="text-sm font-mono font-bold text-gray-900 dark:text-white block truncate">
                        {aeTitle}
                      </code>
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(aeTitle, 'aeTitle')}
                    className="p-1.5 rounded bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 transition-colors flex-shrink-0"
                    title={t('connectionData.copy')}
                  >
                    {copiedField === 'aeTitle' ? (
                      <FiCheck size={14} className="text-green-500" />
                    ) : (
                      <FiCopy size={14} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-between items-center w-full gap-3">
          <div className="flex-1">
            {canManagePacs ? (
              <Button
                onClick={handleManageDevices}
                className="bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 hover:bg-slate-200 dark:hover:bg-gray-600 flex items-center gap-2"
              >
                <FiSettings size={16} />
                {t('connectionData.manageDevices')}
              </Button>
            ) : (
              <div />
            )}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleViewPacsInbox}
              variant="secondary"
              className="flex items-center gap-2"
            >
              {t('connectionData.goToPacsInbox')}
              <FiArrowRight size={16} />
            </Button>
            <Button
              onClick={onClose}
              className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {t('connectionData.gotIt')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
